/**
 * Logic component Based on two displaying components:
 *  - contentEditor :
 *  - mentions :
 * Handling the edition of post giving possibility to lively add link to users.
 */

import { Component, createRef } from 'react';
import ReactDom from 'react-dom';
import ReactDomServer from 'react-dom/server';
import Mentions from './mentions';
import ContentEditor from './contentEditor';
import ImageUpload from './imageUpload';
import classNames from 'classnames';
import { getEditableHTML, getMention, parseNode } from 'src/utils/parserHTML';
import PropTypes from 'prop-types';
import { stripTags } from 'underscore.string';
import api from 'src/utils/api';
import { debounce } from 'lodash';
import { error as logError } from 'loglevel';

const EMPTY_CONTENT = '';
const MAX_MENTIONED_USERS = 15;
const MENTION_SUGGESTIONS = 5;

export default class MessageEditor extends Component {
  static propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    enrichedText: PropTypes.array,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    toggleButtons: PropTypes.func,
  };

  static defaultProps = {
    onFocus: () => {},
    onBlur: () => {},
    onChange: () => {},
    disabled: false,
    toggleButtons: () => {},
  };

  constructor(props) {
    super(props);
    this.state = {
      error: null,
      users: [],
      currentIndex: 0,
      mentionedUsers: [],
      initialHTML: this.props.enrichedText ?
        getEditableHTML(this.props.enrichedText) :
        EMPTY_CONTENT,
      empty: !this.props.enrichedText && !this.props.autoFocus,
    };
    this.contentEditorRef = createRef();
  }

  UNSAFE_componentWillMount() {
    this.search = '';
    this.debouncedFetch = debounce(() => this.updateMentionedUsers(this.fetchUsers), 200);
  }

  componentDidMount() {
    this.updateMentionedUsers();
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  addLink = (user) => {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);

    if (!range) {
      return;
    }

    const node = range.endContainer;
    const offset = range.endOffset;

    range.setStart(node, Math.max(offset - this.search.length - 1, 0));
    range.deleteContents();

    const parser = document.createElement('div');
    parser.innerHTML = ReactDomServer.renderToStaticMarkup(getMention(user, true)) + '&nbsp;';

    let newNode = parser.firstChild;
    while (newNode) {
      range.insertNode(newNode);
      range.setStartAfter(newNode);
      range.setEndAfter(newNode);
      selection.removeAllRanges();
      selection.addRange(range);
      newNode = parser.firstChild;
    }
    this.updateSearch(null);
  }

  canMention = () => this.state.mentionedUsers.length < MAX_MENTIONED_USERS;

  clear = () => {
    ReactDom.findDOMNode(this.contentEditorRef.current).innerHTML = EMPTY_CONTENT;
    this.setState({ empty: true });
  }

  fetchUsers = async () => {
    const { groupId, currentUserId } = this.props;

    if (!currentUserId) return;

    const mentionedUsersIds = this.state.mentionedUsers.map(x => x.userId);
    const searchString = this.search || '';
    const params = {
      q: searchString.toLowerCase(),
      groupid: groupId,
      limit: MENTION_SUGGESTIONS + this.state.mentionedUsers.length,
    };

    this.setState({ error: false });

    try {
      const { data: { payload: response, status } } = await api.auroraGet('user-search/query', { params });
      if (status.success) {
        const users = (response.users || [])
          .filter(({ userId }) => !mentionedUsersIds.includes(userId) && userId !== currentUserId)
          .slice(0, 4);
        this.setState({ users });
      }
    } catch (ex) {
      logError(ex);
      this.setState({ error: true });
    }
  }

  getData = () => {
    const mentionedUsers = this.updateMentionedUsers();
    return {
      ...parseNode(ReactDom.findDOMNode(this.contentEditorRef.current)),
      mentionedUsers,
    };
  }

  keyIntercept = (event) => {
    if (!this.isMentioning() || !this.canMention()) {
      return;
    }

    const { currentIndex, users } = this.state;
    switch ((window.event) ? event.which : event.keyCode) {
      case 40: //up
        this.setState({ currentIndex: Math.min(currentIndex + 1, users.length - 1) });
        event.preventDefault();
        break;
      case 38: //down
        this.setState({ currentIndex: Math.max(currentIndex - 1, 0) });
        event.preventDefault();
        break;
      case 13: //enter
        if (users[currentIndex]) {
          this.addLink(this.state.users[this.state.currentIndex]);
        }
        event.preventDefault();
        break;
    }
  }

  isEmpty = () => {
    const content = ReactDom.findDOMNode(this.contentEditorRef.current);
    this.setState({ empty: content.childNodes.length < 2 && !content.textContent.length });
  }

  isMentioning = () => !!(this.search && this.state.users.length);

  focus = () => this.contentEditorRef.current.focus();
  focusNow = () => this.contentEditorRef.current.focusNow();

  updateMentionedUsers = (callback) => {
    if (!this._mounted) return;
    const content = ReactDom.findDOMNode(this.contentEditorRef.current);
    if (!content || !content.getElementsByTagName) return;

    const mentionedUsers = Array.from(content.getElementsByTagName('a'))
      .map(link => ({
        userId: Number(link.getAttribute('data-id')),
        username: stripTags(link.innerHTML),
      }))
      .filter(user => !!user.userId);

    this.setState({ mentionedUsers }, callback);

    return mentionedUsers;
  }

  updateSearch = (search) => {
    if (this.search === search) {
      return;
    }

    this.search = search || '';
    if (this.search && this.search.length > 0) {
      this.debouncedFetch();
      return;
    }

    this.setState({ error: false, users: [], currentIndex: 0 });
  }

  addEmoji = (emoji) => this.contentEditorRef.current.addEmoji(emoji);

  startMention = () => this.contentEditorRef.current.startMention();

  render() {
    const { className, autoFocus, placeholder, dataTestId} = this.props;
    const contentEditorClassName = classNames(
      className,
      'message-editor',
      {
        empty: this.state.empty,
        disabled: this.props.disabled,
      },
    );

    return (
      <div className={contentEditorClassName}>
        <ContentEditor
          autoFocus={autoFocus}
          dataTestId={dataTestId}
          initialHTML={this.state.initialHTML}
          keyIntercept={this.keyIntercept}
          placeholder={placeholder}
          ref={this.contentEditorRef}
          toggleButtons={this.props.toggleButtons}
          updateSearch={this.updateSearch}
          onBlur={() => {
            this.props.onBlur();
            this.isEmpty();
          }}
          onChange={() => this.props.onChange()}
          onFocus={() => {
            this.props.onFocus();
            this.setState({ empty: false });
          }}
        />
        {this.isMentioning() &&
          <Mentions
            currentIndex={this.state.currentIndex}
            error={this.state.error}
            isLimiting={!this.canMention()}
            select={this.addLink}
            users={this.state.users}
          />
        }
        {this.props.previewImageUrl &&
          <ImageUpload
            description={this.props.previewImageDescription}
            url={this.props.previewImageUrl}
            onAddImageDescription={this.props.onAddImageDescription}
            onPreviewRemove={this.props.onPreviewRemove}
          />
        }
      </div>
    );
  }
}
